import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Monitor, Clock } from 'lucide-react';
import { RouletteWheel } from '../components/RouletteWheel';

export const Hero = () => {
  const scrollToRoulette = () => {
    const el = document.getElementById('roulette');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToFeatures = () => {
    const el = document.getElementById('features');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-24 overflow-hidden">
      {/* Background gradients */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#131620] via-[#0b0d14] to-[#080a0f]" />
      
      {/* Decorative glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold-600/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="container mx-auto px-6 md:px-12 relative z-10 h-full flex flex-col lg:flex-row items-center justify-between">
        
        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-2xl lg:w-[45%] flex flex-col items-start text-left mt-12 lg:mt-0 relative z-20"
        >
          <div className="inline-flex items-center px-4 py-1.5 rounded-full border border-gold-500/30 mb-8 backdrop-blur-sm">
            <span className="text-gold-400 text-[10px] md:text-xs font-bold tracking-widest uppercase">PREMIUM ROULETTE EXPERIENCE</span>
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-[75px] font-serif text-white mb-6 leading-[1.1] tracking-tight uppercase">
            SPIN INTO <br />
            THE <span className="text-gold-500">ULTIMATE</span> <br />
            <span className="text-gold-500">ROULETTE</span> <br />
            EXPERIENCE
          </h1>

          <p className="text-lg text-gray-400 mb-10 max-w-md leading-relaxed font-sans">
            Experience the timeless thrill of Roulette through a beautifully designed, immersive gaming platform.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-start items-center w-full sm:w-auto font-sans">
            <button
              onClick={scrollToRoulette}
              className="px-8 py-3.5 bg-gradient-to-r from-[#e3c66f] to-[#b38e2d] text-black font-bold tracking-wider rounded-full shadow-[0_0_20px_rgba(212,175,55,0.2)] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all w-full sm:w-auto text-sm"
            >
              PLAY ROULETTE
            </button>
            <button
              onClick={scrollToFeatures}
              className="flex items-center justify-center gap-2 px-8 py-3.5 bg-transparent border border-gray-700 hover:border-gold-500 text-gold-500 rounded-full font-bold tracking-wider transition-all w-full sm:w-auto text-sm"
            >
              EXPLORE
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Features list at bottom left */}
          <div className="mt-20 flex flex-wrap items-center gap-8 text-sm text-gray-400 font-sans">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-gold-500" />
              <span>Premium Interface</span>
            </div>
            <div className="flex items-center gap-2">
              <Monitor className="w-4 h-4 text-gold-500" />
              <span>Smooth Gameplay</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gold-500" />
              <span>24/7 Support</span>
            </div>
          </div>
        </motion.div>

        {/* Right Content - Hero Image/Wheel */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="lg:w-[55%] relative hidden lg:flex justify-end items-center z-0"
        >
          <div className="relative w-[500px] h-[500px]">
            <div className="absolute inset-0 bg-gold-500/10 blur-[100px] rounded-full pointer-events-none" />
            
            <div className="w-full h-full pointer-events-none drop-shadow-2xl">
              <RouletteWheel isSpinning={false} lastWinningNumber={null} isAmbient={true} />
            </div>
            
            {/* Floating Chips */}
            <motion.div 
              animate={{ y: [0, -15, 0] }} 
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="absolute top-[10%] left-[0%] w-16 h-16 rounded-full bg-[#0a5c36] border-[4px] border-[#0e8f52] shadow-2xl flex items-center justify-center font-bold text-white z-20 text-lg"
            >
              <div className="absolute inset-1 rounded-full border border-dashed border-white/40" />
              $25
            </motion.div>
            
            <motion.div 
              animate={{ y: [0, -10, 0] }} 
              transition={{ repeat: Infinity, duration: 3, ease: "easeInOut", delay: 1 }}
              className="absolute bottom-[5%] left-[10%] w-12 h-12 rounded-full bg-[#8c1c1c] border-[3px] border-[#b92b2b] shadow-2xl flex items-center justify-center font-bold text-white z-20 text-sm"
            >
              <div className="absolute inset-1 rounded-full border border-dashed border-white/40" />
              $5
            </motion.div>
            
            <motion.div 
              animate={{ y: [0, -12, 0] }} 
              transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 2 }}
              className="absolute top-[40%] right-[-5%] w-[70px] h-[70px] rounded-full bg-charcoal-900 border-[4px] border-gray-600 shadow-2xl flex items-center justify-center font-bold text-[#e3c66f] z-20 text-xl"
            >
              <div className="absolute inset-1 rounded-full border border-dashed border-white/20" />
              $100
            </motion.div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};
